import { useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Backward memory for report pages.
 *
 * Reports keep their filters + generated results in local state, which is lost
 * when you click a document (the report unmounts) and click Back (it re-mounts
 * fresh). This hook stashes a report's view snapshot in an in-memory, session-
 * scoped store so Back restores the EXACT report — filters AND the already-
 * generated rows — WITHOUT re-fetching. That means:
 *   • 0 extra Firebase reads on Back (the whole point of Phase 2), and
 *   • the user lands back exactly where they were.
 *
 * Design notes:
 *   • Keyed by report + company + financial-year, so switching company or FY
 *     naturally starts the report fresh (never shows another scope's data).
 *   • Purely in-memory — cleared on a hard browser refresh (no storage bloat,
 *     no stale data across sessions).
 *   • Fully OPT-IN and ADDITIVE: a report that doesn't call this is unchanged;
 *     a report that does still behaves identically on first visit (no snapshot
 *     yet → falls back to its defaults). It never alters how data is fetched.
 *
 * Usage (per report):
 *   const [saved, saveMemory] = useReportMemory('ledger');
 *   const [dateFrom, setDateFrom] = useState(() => saved?.dateFrom ?? '');
 *   ...
 *   // inside the Generate handler, after results are set:
 *   saveMemory({ dateFrom, dateTo, ..., entries, hasSearched: true });
 */

const _store = new Map();

export default function useReportMemory(reportKey) {
  const { activeCompany, activeFinancialYear } = useAuth();
  const fullKey = `${reportKey}::${activeCompany?.id || ''}::${activeFinancialYear?.id || ''}`;

  // Capture the saved snapshot ONCE at mount (per key) so useState initializers
  // can restore it synchronously — no flash, no fetch. If the company/FY changes
  // mid-session the key changes and we re-read for the new scope.
  const snapRef = useRef(undefined);
  const keyRef = useRef(fullKey);
  if (snapRef.current === undefined || keyRef.current !== fullKey) {
    snapRef.current = _store.get(fullKey) || null;
    keyRef.current = fullKey;
  }

  const save = useCallback((snapshot) => {
    if (snapshot && typeof snapshot === 'object') _store.set(fullKey, snapshot);
  }, [fullKey]);

  return [snapRef.current, save];
}
